import { ContactsTableData } from "../components/ManageContacts/utils/types";
import { TEMPLATE_MODALITY_ENUM } from "../components/ManageTemplates/utils/constants";

export interface CampaignDashboardTab {
  id: string;
  label: string;
  path: string;
}
export interface Campaign {
  id: string;
  name: string;
  status: string;
  modality_info: Array<{
    channel: string;
    template: {
      id: string;
      name: string;
    };
  }>;
}

export type TransformCampaignToOption = (
  campaign: Campaign,
  getChannelIcon: (channel: string) => React.ReactNode,
) => {
  id: string;
  value: string;
  content: React.ReactElement;
};

export type CampaignPageTypes =
  | "all"
  | "templates"
  | "contacts"
  | "segments"
  | "logs"
  | "marketing-consent";

export type StepName =
  | "category"
  | "details"
  | "reachouts"
  | "reachout-config"
  | "contacts"
  | "preview";

export type CampaignStatus =
  | "running"
  | "scheduled"
  | "completed"
  | "draft"
  | "failed"
  | "terminated";

export interface CampaignApiResponse {
  campaigns: {
    id: string;
    name: string;
    status: CampaignStatus;
    modality_info: {
      channel: string;
      run_time: string;
      template?: {
        id?: string;
        name?: string;
      };
    }[];
    start_time: string;
    completed_at: string;
    timezone: string;
    use_case: string;
    category: string;
    display_usecase_name?: string | null;
    display_category_name?: string | null;
    display_branch_name?: string | null;
    audience_data: {
      audience_count: number;
      audience_data_link: string;
      file_name: string;
      file_id: string;
      file_key: string;
      valid_audience_count?: number;
      invalid_audience_count?: number;
    };
    updated_at: string;
    branch: string;
    created_by: {
      user_name: string;
    };
    created_at: string;
    progress?: {
      completed_count: number;
      total_count: number;
      completed_percentage: number;
      total_modalities: number;
    };
    language: {
      name: string;
      language: string;
    };
  }[];

  total: number;
  page: number;
  page_size: number;
}

export interface CampaignApiResponseWithProgress {
  id: string;
  name: string;
  status: CampaignStatus;
  start_time: string;
  completed_at: string;
  timezone: string;
  modality_info: {
    channel: string;
    run_time: string;
    template?: {
      id?: string;
      name?: string;
    };
  }[];
  use_case: string;
  category: string;
  audience_data: {
    audience_count: number;
    audience_data_link: string;
  };
  updated_at: string;
  branch: string;
  created_by: {
    user_name: string;
  };
  created_at: string;
  progress: {
    completed_count: number;
    total_count: number;
    completed_percentage: number;
    total_modalities: number;
  };
}
export interface ContactsFileDropboxProps {
  dropboxTitle: string;
  secondaryText: string;
  maxSizeInMB: number; //in KB
  handleFileUpload: (file: File | null) => void;
  handleSubmitUpload: () => Promise<ContactsTableData | null>;
  file: File | null;
  Icon?: React.FC<React.SVGProps<SVGSVGElement>> | React.FC;
  iconClassName?: string;
  isInCampaignFlow?: boolean;
  handlePreview?: (data: ContactsTableData) => void;
}

export interface CampaignDashboardTab {
  id: string;
  label: string;
  path: string;
}

export interface CampaignLog {
  _id: string;
  campaign_log_operation: string;
  campaign_id: string;
  name: string;
  client_code: string;
  name_update?: {
    old_name: string;
    new_name: string;
  } | null;
  audience_data_update?: {
    old_audience_data: {
      audience_data_link: string;
      file_name: string;
      file_key: string;
    };
    new_audience_data: {
      audience_data_link: string;
      file_name: string;
      file_key: string;
    };
  } | null;
  reach_out_added?: {
    channel: string;
    template: {
      id: string;
      name: string;
    };
    run_time: string;
    delay: number;
  } | null;
  reach_out_removed?: {
    channel: string;
    template: {
      id: string;
      name: string;
    };
    run_time: string;
    delay: number;
  } | null;
  status_update?: {
    old_status: string;
    new_status: string;
  } | null;
  execution_type_update?: {
    old_use_case?: string;
    new_use_case?: string;
  } | null;
  timestamp: string;
  user_id: string;
  user_name: string;
  template_info?: {
    name: string;
    id: string;
  };
  template_update?: string[];
  contact_csv_info?: {
    name: string;
    id: string;
  };
}

export interface ActivityLogUserData {
  name: string;
  email: string;
  phone: string;
  due_amount: string;
  whatsapp_phone: string;
  room_num: string;
  property_name: string;
  due_date: string;
}

export interface ActivityLogTemplateInfo {
  id: string;
  name: string;
}

export interface CampaignActivityLog {
  id: string;
  campaign_id: string;
  client_code: string;
  channel: string;
  user_data: ActivityLogUserData;
  status: string;
  failed_reason: string | null;
  template_info: ActivityLogTemplateInfo;
  timestamp: string;
}

export interface CampaignCategoryCardProps {
  category: CampaignCategoryType;
  isSelected: boolean;
  type?: "collapsed" | "expanded";
  onSelect?: () => void;
  disabled?: boolean;
  testId?: string;
}

export type CampaignCategoryType = {
  key: string;
  display_name: string;
  description?: string;
  icon?: React.ElementType;
};

export interface ManageTemplatesProps {
  setShowTabs: (show: boolean) => void;
}

export type EmailTemplateAPIResponse = {
  branch: string | null;
  builder_design: object;
  category: string;
  content: string;
  created_at: string;
  created_by: {
    id: string;
    name: string;
  };
  id: string;
  modality: TEMPLATE_MODALITY_ENUM.EMAIL;
  subject: string;
  title: string;
  updated_at: string;
  use_case: string;
  variables: string[];
  isVfTemplate?: boolean;
  template_preview_url?: string;
  display_usecase_name?: string | null;
  display_category_name?: string | null;
  display_branch_name?: string | null;
  is_favorite?: boolean;
};

export type CallTemplateAPIResponse = {
  assistant_name: string;
  branch: string | null;
  call_context: string;
  call_script: string;
  category: string;
  created_at: string;
  created_by: {
    id: string;
    name: string;
  };
  end_message: string;
  id: string;
  initial_message: string;
  modality: TEMPLATE_MODALITY_ENUM.CALL;
  title: string;
  updated_at: string;
  use_case: string;
  variables: string[];
  voice_name: string;
  voice_id: string;
  voice_url: string;
  voice_speed: string;
  voice_provider: string;
  isVfTemplate?: boolean;
  template_preview_url?: string;
  display_usecase_name?: string | null;
  display_category_name?: string | null;
  display_branch_name?: string | null;
  is_favorite?: boolean;
};

export interface UrlButton {
  type?: "url" | string;
  text?: string;
  url?: string;
}

export interface CopyOfferCodeButton {
  type?: "copy_offer_code" | string;
  text?: string;
  copy_offer_code?: string;
}

export interface PhoneNumberButton {
  type?: "phone_number" | string;
  text?: string;
  phone_number?: string;
  country_code?: string;
}

export interface QuickReplyButton {
  type?: "quick_reply" | string;
  text?: string;
}

export interface TemplateHeader {
  type?: number;
  header_type_string?: "text" | "image" | "video" | "document" | string;
  type_string?: string;
  text?: string | null;
  link?: string;
  media_header_id?: string;
  media_from_pc?: string;
  media_url?: string;
}

export type TemplateButton =
  | UrlButton
  | CopyOfferCodeButton
  | PhoneNumberButton
  | QuickReplyButton;

export enum WatiTemplateApprovalStatus {
  DRAFT = 0,
  PENDING = 1,
  APPROVED = 2,
  REJECTED = 3,
  DELETED = 4,
  PENDING_INTERNAL = 5,
  DISABLED = 6,
  PAUSED = 7,
}

export enum WatiTemplateQuality {
  UNKNOWN = 0,
  GREEN = 1,
  RED = 2,
  YELLOW = 3,
}

export enum WatiTemplateCategory {
  MARKETING = "MARKETING",
  UTILITY = "UTILITY",
  AUTHENTICATION = "AUTHENTICATION",
}

export enum WatiTemplateSubCategory {
  STANDARD = "STANDARD",
  CAROUSEL = "CAROUSEL",
  CATALOG = "CATALOG",
  CHECKOUT_BUTTON = "CHECKOUT_BUTTON",
  ORDER_STATUS = "ORDER_STATUS",
  LIMITED_TIME_OFFER = "LIMITED_TIME_OFFER",
}

export type WhatsappTemplateAPIResponse = {
  id: string;
  title: string;
  created_by: {
    id: string;
    name: string;
  };
  created_at: string;
  updated_at: string;
  variables: string[];
  modality: TEMPLATE_MODALITY_ENUM.WHATSAPP;
  template_name: string;
  broadcast_name?: string;
  body?: string;
  buttons?: TemplateButton[];
  footer?: string;
  header?: TemplateHeader;
  wati_category?: WatiTemplateCategory | null;
  wati_sub_category?: WatiTemplateSubCategory | null;
  wati_approval_status?: WatiTemplateApprovalStatus | null;
  wati_quality?: WatiTemplateQuality | null;
  use_case?: string;
  category?: string;
  branch?: string;
  isVfTemplate?: boolean;
  template_preview_url?: string;
  language?: string;
  display_usecase_name?: string | null;
  display_category_name?: string | null;
  display_branch_name?: string | null;
  is_favorite?: boolean;
};

export type SmsTemplateAPIResponse = {
  id: string;
  title: string;
  created_by: {
    id: string;
    name: string;
  };
  created_at: string;
  updated_at: string;
  variables: string[];
  modality: TEMPLATE_MODALITY_ENUM.SMS;
  content: string;
  use_case?: string;
  category?: string;
  branch?: string;
  isVfTemplate?: boolean;
  template_preview_url?: string;
  display_usecase_name?: string | null;
  display_category_name?: string | null;
  display_branch_name?: string | null;
  is_favorite?: boolean;
};

export interface CampaignDetailsFormValues {
  category: string;
  name: string;
  use_case: string;
  branch: string | null;
}

export interface UseCase {
  key: string;
  display_name: string;
}
export interface Branch {
  key: string;
  display_name: string;
}

export interface CampaignDataForSteps {
  id?: string;
  name?: string;
  use_case?: string;
  category?: string;
  timezone?: string;
  modality_info?: Array<{
    channel: string;
    run_time?: string;
    template?: {
      id?: string;
      name?: string;
    };
    is_saved?: boolean;
  }>;
  audience_data?: {
    audience_count?: number;
    audience_data_link?: string;
    file_name?: string;
    file_id?: string;
    file_key?: string;
  };
}
